import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ReportsService } from '../../../../reports.service';
import {
  areColumnsSynonymous,
  buildJoinTypeOptions,
  ColumnMapping,
  DatasetColumn,
  DatasetConfig,
  DatasetJoin,
  DatasourceOption,
  findDatasetColumn,
  isJoinFullyConfigured,
  JoinTypeOption,
  mapApiColumn
} from '../select-datasource-modal.model';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { DatasetColumnTableComponent } from '../dataset-column-table/dataset-column-table.component';

let joinSequence = 0;
let columnMappingSequence = 0;

@Component({
    selector: 'app-dataset-card',
    templateUrl: './dataset-card.component.html',
    styleUrl: './dataset-card.component.scss',
    imports: [FormsModule, MatSelect, MatOption, MatIcon, DatasetColumnTableComponent]
})
export class DatasetCardComponent {
  @Input() dataset!: DatasetConfig;
  @Input() index = 0;
  @Input() allDatasets: DatasetConfig[] = [];
  @Input() datasourceOptions: DatasourceOption[] = [];
  @Input() usedTableNames: string[] = [];
  @Input() canRemove = false;

  @Output() remove = new EventEmitter<void>();

  constructor(private reportsService: ReportsService) {}

  onDatasourceSelected(tableName: string): void {
    this.dataset.table_name = tableName;
    this.dataset.dataset_name = this.datasourceOptions.find(option => option.table_name === tableName)?.dataset_name || '';
    this.dataset.columns = [];
    this.dataset.columnsLoaded = false;
    this.dataset.joins = [];
    this.dataset.relationshipsOpen = false;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
  }

  onToggleManageColumns(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.manageColumnsOpen = !this.dataset.manageColumnsOpen;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
  }

  get allColumnsSelected(): boolean {
    return this.dataset.columns.length > 0 && this.dataset.columns.every(column => column.selected);
  }

  onUseAllColumns(): void {
    const shouldSelectAll = this.dataset.columns.some(column => !column.selected);
    this.dataset.columns.forEach(column => (column.selected = shouldSelectAll));
  }

  get availableJoinTargets(): DatasetConfig[] {
    return this.allDatasets.filter(dataset => dataset.id !== this.dataset.id);
  }

  get relationshipsConfigured(): boolean {
    return this.dataset.joins.length > 0 && this.dataset.joins.every(join => this.isJoinConfigured(join));
  }

  isJoinConfigured(join: DatasetJoin): boolean {
    return isJoinFullyConfigured(join, this.dataset, this.allDatasets);
  }

  // Cached per join so the *ngFor array reference stays stable across change-detection cycles -
  // rebuilding a fresh array on every call here froze the panel (mat-select kept re-rendering its options).
  private joinTypeOptionsCache = new Map<string, { key: string; options: JoinTypeOption[] }>();

  getJoinTypeOptions(join: DatasetJoin): JoinTypeOption[] {
    const target = this.getDatasetById(join.targetDatasetId);
    const targetName = target?.dataset_name || target?.table_name || '';
    const currentName = this.dataset.dataset_name || this.dataset.table_name || '';
    const cacheKey = `${targetName}|${currentName}`;
    const cached = this.joinTypeOptionsCache.get(join.id);
    if (cached && cached.key === cacheKey) {
      return cached.options;
    }
    const options = buildJoinTypeOptions(targetName, currentName);
    this.joinTypeOptionsCache.set(join.id, { key: cacheKey, options });
    return options;
  }

  // Null when the pair is valid or incomplete; a message once both columns are picked but don't refer to the same field.
  getMappingError(join: DatasetJoin, mapping: ColumnMapping): string | null {
    if (!mapping.leftColumn || !mapping.rightColumn) {
      return null;
    }
    const target = this.getDatasetById(join.targetDatasetId);
    const leftColumn = findDatasetColumn(target, mapping.leftColumn);
    const rightColumn = findDatasetColumn(this.dataset, mapping.rightColumn);
    if (leftColumn && rightColumn && !areColumnsSynonymous(leftColumn, rightColumn)) {
      return `"${leftColumn.name}" and "${rightColumn.name}" don't appear to be the same field - map columns that represent the same data across tables.`;
    }
    return null;
  }

  getDatasetById(id: string | null): DatasetConfig | undefined {
    return this.allDatasets.find(dataset => dataset.id === id);
  }

  filterColumns(columns: DatasetColumn[] | undefined, search: string | undefined): DatasetColumn[] {
    const list = columns || [];
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter(column => column.name.toLowerCase().includes(term));
  }

  onToggleRelationships(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.relationshipsOpen = !this.dataset.relationshipsOpen;
    if (this.dataset.relationshipsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
      if (!this.dataset.joins.length) {
        this.onAddJoin();
      }
    }
  }

  trackByJoinId(_: number, join: DatasetJoin): string {
    return join.id;
  }

  trackByMappingId(_: number, mapping: ColumnMapping): string {
    return mapping.id;
  }

  onAddJoin(): void {
    const join = this.createJoin();
    this.dataset.joins.push(join);
    const target = this.getDatasetById(join.targetDatasetId);
    if (target) {
      this.ensureDatasetColumnsLoaded(target);
    }
  }

  onRemoveJoin(join: DatasetJoin): void {
    this.dataset.joins = this.dataset.joins.filter(item => item.id !== join.id);
    this.joinTypeOptionsCache.delete(join.id);
  }

  onJoinTargetChange(join: DatasetJoin): void {
    join.columnMappings = [this.createColumnMapping()];
    const target = this.getDatasetById(join.targetDatasetId);
    if (target) {
      this.ensureDatasetColumnsLoaded(target);
    }
  }

  onAddColumnMapping(join: DatasetJoin): void {
    join.columnMappings.push(this.createColumnMapping());
  }

  onRemoveColumnMapping(join: DatasetJoin, mapping: ColumnMapping): void {
    join.columnMappings = join.columnMappings.filter(item => item.id !== mapping.id);
  }

  private createJoin(): DatasetJoin {
    joinSequence += 1;
    return {
      id: `join-${joinSequence}`,
      targetDatasetId: this.availableJoinTargets[0]?.id ?? null,
      joinType: null,
      columnMappings: [this.createColumnMapping()]
    };
  }

  private createColumnMapping(): ColumnMapping {
    columnMappingSequence += 1;
    return { id: `mapping-${columnMappingSequence}`, leftColumn: null, rightColumn: null };
  }

  private ensureDatasetColumnsLoaded(target: DatasetConfig): void {
    if (!target.table_name || target.columnsLoaded || target.columnsLoading) {
      return;
    }
    target.columnsLoading = true;
    this.reportsService.getDatasourceColumns({ type: 'columnInfo', tableName: target.table_name, datasetName: target.dataset_name }).subscribe({
      next: (columns: any[]) => {
        target.columns = (columns || []).map(column => mapApiColumn(column));
        target.columnsLoaded = true;
        target.columnsLoading = false;
      },
      error: () => {
        target.columnsLoading = false;
      }
    });
  }
}
